import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { CnForm, CnFormField, CnFormGrid, formilyCore, Dropdown, CnAsyncSelect, CnButton, CnMenuSubMenu, CnMenuItem, CnMenu, } from '@alife/cn-ui';
import isEqual from 'lodash/isEqual';
import { useMemoizedFn } from 'ahooks';
import $i18n from 'panda-i18n';
import CnOneSearchContext from '../context';
import DateDialog from './date-dialog';
import classNames from 'classnames';
const { createForm } = formilyCore;
const useCompare = (value) => {
    const ref = React.useRef(null);
    if (!isEqual(value, ref.current)) {
        ref.current = value;
    }
    return ref.current;
};
function SelectField(props) {
    const { onChange, value: formatValue = {}, ...other } = props;
    const isSingle = props.mode !== 'multiple';
    let value = formatValue.values;
    if (isSingle) {
        value = Array.isArray(formatValue.values) ? formatValue.values[0] : null;
    }
    return (_jsx(CnAsyncSelect, { value: value, placeholder: $i18n.get({ id: 'PleaseSelect', dm: '请选择' }), hasClear: true, onChange: (newValue, _actionType, item = {}) => {
            if (isSingle) {
                onChange({
                    values: [newValue],
                    labels: [item.label],
                });
            }
            else {
                onChange({
                    values: newValue,
                    labels: item?.map && item?.map((it) => it?.label),
                });
            }
        }, ...(other || {}) }));
}
function DimensionTimeMenu(props) {
    const { onChange, value: formatValue = {}, type, ...other } = props;
    const value = formatValue.values;
    const menuList = {
        menuKey: type,
        munuLabel: type === 'DimensionDateMenuField'
            ? $i18n.get({ id: 'DateGranularity', dm: '日期粒度' })
            : $i18n.get({ id: 'TimeGranularity', dm: '时间粒度' }),
        children: type === 'DimensionDateMenuField'
            ? [
                {
                    key: $i18n.get({ id: 'Year', dm: '年' }),
                    text: $i18n.get({ id: 'Year', dm: '年' }),
                },
                {
                    key: $i18n.get({ id: 'Month', dm: '月' }),
                    text: $i18n.get({ id: 'Month', dm: '月' }),
                },
                {
                    key: $i18n.get({ id: 'Day', dm: '日' }),
                    text: $i18n.get({ id: 'Day', dm: '日' }),
                },
            ]
            : [
                {
                    key: $i18n.get({ id: 'Year', dm: '年' }),
                    text: $i18n.get({ id: 'Year', dm: '年' }),
                },
                {
                    key: $i18n.get({ id: 'Month', dm: '月' }),
                    text: $i18n.get({ id: 'Month', dm: '月' }),
                },
                {
                    key: $i18n.get({ id: 'Day', dm: '日' }),
                    text: $i18n.get({ id: 'Day', dm: '日' }),
                },
                {
                    key: $i18n.get({ id: 'When', dm: '时' }),
                    text: $i18n.get({ id: 'When', dm: '时' }),
                },
            ],
    };
    return (_jsx(CnMenu, { selectedKeys: value, selectMode: 'multiple', defaultOpenKeys: menuList.menuKey, onSelect: (keys, item) => {
            const _keys = [];
            if (keys.length) {
                _keys.push(keys[keys.length - 1]);
            }
            onChange({
                values: _keys,
                labels: _keys.length ? [item.props.children] : [],
            });
        }, ...(other || {}), popupClassName: 'search-box-time-menu-popup', children: _jsx(CnMenuSubMenu, { label: menuList.munuLabel, selectable: false, children: menuList.children.map((item) => (_jsx(CnMenuItem, { children: item.text }, item.key))) }, menuList.menuKey) }));
}
function QuickFieldForm({ onChange, fieldConfig, value, trigger, defaultVisible, disabled = false, }) {
    const { containerRef, components, schema } = useContext(CnOneSearchContext);
    const [labels, setLabels] = useState(value?.labels || []);
    const [visible, setVisible] = useState(defaultVisible);
    const selectWrapId = React.useMemo(() => {
        return `one_search_select_popup_container_el_${new Date().getTime()}`;
    }, []);
    const { formSchema, fieldSchema } = React.useMemo(() => {
        // @ts-ignore
        const oldFieldSchema = schema?.properties[fieldConfig.key];
        if (oldFieldSchema && typeof oldFieldSchema === 'object') {
            const oldOnFieldChange = oldFieldSchema['x-component-props']?.onChange;
            let componentOtherProperties = {};
            if (oldFieldSchema['x-component'] === 'CnAsyncSelect' ||
                oldFieldSchema['x-component'] === 'Select') {
                componentOtherProperties = {
                    ...componentOtherProperties,
                    visible: true,
                    popupContainer: selectWrapId,
                    className: `cn-search-custom-select`,
                };
            }
            const extProperties = {
                title: '',
                required: false,
                'x-decorator': 'CnFormItem',
                'x-component-props': {
                    ...(oldFieldSchema['x-component-props'] || {}),
                    onChange: (...args) => {
                        if (typeof oldOnFieldChange === 'function') {
                            oldOnFieldChange(...args);
                        }
                        const isSelect = ['CnAsyncSelect', 'Select'].includes(oldFieldSchema['x-component']);
                        const isDatePicker = oldFieldSchema['x-component'] === 'CnDatePickerPro';
                        const isRangeDatePicker = oldFieldSchema['x-component'] === 'CnRangeDatePickerPro';
                        if (isSelect) {
                            const isMultiSelect = isSelect &&
                                oldFieldSchema['x-component-props']?.mode === 'multiple';
                            if (isMultiSelect) {
                                const items = args[2];
                                if (Array.isArray(items)) {
                                    setLabels(items.map((it) => it?.label));
                                }
                            }
                            else {
                                const item = args[2];
                                setLabels([item?.label]);
                            }
                        }
                        else if (isRangeDatePicker) {
                            const newValue = args[0];
                            if (Array.isArray(newValue)) {
                                setLabels([newValue.join(' - ')]);
                            }
                        }
                        else if (isDatePicker) {
                            const newValue = args[0];
                            if (newValue) {
                                setLabels([newValue]);
                            }
                        }
                        window.setTimeout(() => {
                            handleSubmit(false);
                        }, 10);
                    },
                    ...componentOtherProperties,
                },
            };
            const newFieldSchema = {
                ...oldFieldSchema,
                ...extProperties,
            };
            return {
                formSchema: {
                    type: 'object',
                    properties: {
                        [`${fieldConfig.key}`]: newFieldSchema,
                    },
                },
                fieldSchema: newFieldSchema,
            };
        }
        return {};
    }, [useCompare(fieldConfig.key), useCompare(schema)]);
    const renderField = () => {
        if (fieldConfig.type === 'SelectField') {
            fieldConfig.fieldComponentProps = {
                ...fieldConfig.fieldComponentProps,
                visible: true,
                popupContainer: selectWrapId,
                className: `cn-search-custom-select`,
            };
        }
        if (fieldConfig.type === 'DimensionTimeMenuField' ||
            fieldConfig.type === 'DimensionDateMenuField') {
            fieldConfig.fieldComponentProps = {
                ...fieldConfig.fieldComponentProps,
                visible,
                popupContainer: selectWrapId,
                className: `cn-search-custom-select`,
                type: fieldConfig.type,
            };
        }
        if (typeof fieldConfig.render === 'function') {
            const renderResult = fieldConfig.render(fieldConfig);
            // 如果返回了 ReactElement 则直接返回，否则继续渲染
            if (renderResult && React.isValidElement(renderResult)) {
                return renderResult;
            }
        }
        switch (fieldConfig.type) {
            case 'RangePickerField':
            case 'RangeDatePickerField':
                return (_jsx(DateDialog, { isDate: fieldConfig.type === 'RangeDatePickerField', ...fieldConfig.fieldComponentProps, trigger: trigger, closeDropdown: () => {
                        setVisible(false);
                    }, onChange: onChange, value: value }));
            case 'SelectField':
                return _jsx(SelectField, { ...fieldConfig.fieldComponentProps });
            case 'DimensionTimeMenuField':
            case 'DimensionDateMenuField':
                return _jsx(DimensionTimeMenu, { ...fieldConfig.fieldComponentProps });
            default:
                return null;
        }
    };
    const form = React.useMemo(() => {
        let initValue = value;
        if (fieldSchema) {
            initValue = value.values || [];
            const componentName = fieldSchema['x-component'];
            if (componentName === 'CnDatePickerPro' ||
                (['CnAsyncSelect', 'Select'].includes(componentName) &&
                    fieldSchema['x-component-props']?.mode !== 'multiple')) {
                initValue = Array.isArray(initValue) ? initValue[0] : undefined;
            }
        }
        return createForm({
            initialValues: {
                [fieldConfig.key]: initValue,
            },
        });
    }, [fieldConfig.key, value, fieldSchema]);
    const handleVisibleChange = useMemoizedFn((newVisible, type) => {
        if (type !== 'docClick') {
            setVisible(newVisible);
        }
    });
    const handleSubmit = useMemoizedFn((isClose) => {
        if (form.valid) {
            const fieldValue = form.values[fieldConfig.key];
            if (formSchema) {
                onChange(Array.isArray(fieldValue) ? fieldValue : [fieldValue], labels);
            }
            else {
                onChange(fieldValue.values || [], fieldValue.labels || []);
            }
            if (isClose) {
                setVisible(false);
            }
        }
    });
    useEffect(() => {
        const handler = (e) => {
            const dropdownNodes = document.querySelectorAll('.cn-next-overlay-wrapper');
            let flag = true;
            dropdownNodes.forEach((node) => {
                if (node?.contains(e.target)) {
                    flag = false;
                }
            });
            if (flag && e.target?.className === 'cn-next-menu-item-text') {
                flag = false;
            }
            if (!containerRef?.current?.contains(e.target) && flag) {
                setVisible(false);
            }
        };
        document.addEventListener('click', handler, true);
        return () => {
            document.removeEventListener('click', handler, true);
        };
    }, [containerRef]);
    const fieldContent = renderField();
    const isNode = !formSchema && React.isValidElement(fieldContent);
    return fieldContent ? (_jsx(Dropdown, { disabled: disabled, trigger: trigger, triggerType: ['click'], visible: visible, onVisibleChange: handleVisibleChange, className: classNames('cn-one-search-quick-field-form-dropdown', {
            'cn-one-search-custom-select-wrap': fieldConfig.type !== 'RangePickerField' &&
                fieldConfig.type !== 'RangeDatePickerField',
        }), offset: [0, 4], children: fieldConfig.type === 'RangePickerField' ||
            fieldConfig.type === 'RangeDatePickerField' ? (fieldContent) : (_jsxs("div", { className: classNames('cn-next-dialog cn-next-dialog-v2 cn-ui-dialog cn-one-search-quick-field-form', {
                'cn-one-search-quick-field-auto-width-form': fieldConfig.type === 'DimensionDateMenuField' ||
                    fieldConfig.type === 'DimensionTimeMenuField',
            }), children: [isNode ? (_jsx(CnForm, { form: form, children: _jsx(CnFormGrid, { children: _jsx(CnFormField, { name: fieldConfig.key, children: fieldContent }) }) })) : (_jsx(CnForm, { form: form, components: components, schema: formSchema })), _jsx("div", { className: 'cn-search-custom-select-container', children: _jsx("div", { className: 'cn-search-custom-select-popup-container', id: selectWrapId }) }), !formSchema ? (_jsxs("div", { className: 'form-footer', children: [_jsx(CnButton, { onClick: () => {
                                setVisible(false);
                            }, children: $i18n.get({ id: 'Cancel', dm: '取消' }) }), _jsx(CnButton, { htmlType: 'submit', type: 'primary', onClick: handleSubmit, children: $i18n.get({ id: 'Determine', dm: '确定' }) })] })) : null] })) })) : (trigger);
}
QuickFieldForm.displayName = 'OneSearch.QuickFieldForm';
export default QuickFieldForm;
